"use client";

import { useCallback, useState, useEffect } from "react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import type { MainServiceLabel } from "@/components/home/hero/constants";
import { usePathname, useRouter } from "@/i18n/navigation";
import { getSearchPathByLabel } from "@/utils/constants/services/serviceCatalog";
import {
  type SearchServiceKey,
  toSearchServiceKey,
} from "@/components/search-form/search.modules.type";
import { useLocale } from "next-intl";
import dayjs from "dayjs";
import { getStableTodayIso } from "@/lib/date";
import { openInNewTabWithHistory } from "@/lib/new-tab-history";

type SearchSubmitHotelState = {
  adults: number;
  children: number;
  rooms: number;
  start: string;
  end: string;
  search: string;
  hasCoordinates: boolean;
  lat: string | number | undefined;
  lng: string | number | undefined;
  locationId: string;
};

type SearchSubmitSharedState = {
  locationId: string;
  search?: string;
  lat: string | number | undefined;
  lng: string | number | undefined;
  start: string;
  end: string;
  date: string;
  vehicleType: string;
  pickupType: string;
};

/**
 * - `newTab`: open the results in a new browser tab (location changed / from non-search pages)
 * - `currentTab`: update the URL of the current page so results refetch in place
 */
export type SearchSubmitMode = "currentTab" | "newTab";

type UseSearchSubmitParams = {
  activeService: SearchServiceKey | MainServiceLabel | string | undefined;
  searchParams: ReadonlyURLSearchParams;
  hotel: SearchSubmitHotelState;
  shared: SearchSubmitSharedState;
  onSearchSuccess?: () => void;
  targetPathOverride?: string;
};

/*************************************
 * Query tuples
 *********************************** */

type QueryValue = string | number | null | undefined;

/** `[key, value]` — an empty value means "remove this key from the URL". */
type QueryEntry = readonly [key: string, value: QueryValue];

/** `[when, entries]` — the first rule whose `when` is true wins. */
type QueryRule = readonly [when: boolean, entries: readonly QueryEntry[]];

/** `[lat, lng]` normalized to strings (or undefined). */
type CoordsTuple = readonly [lat: string | undefined, lng: string | undefined];

type SubmitContext = {
  hotel: SearchSubmitHotelState;
  shared: SearchSubmitSharedState;
  hotelCoords: CoordsTuple;
  sharedCoords: CoordsTuple;
  hotelSearch: string;
  sharedSearch: string;
};

const isEmpty = (value: QueryValue) =>
  value === undefined || value === null || value === "";

const toCoord = (value: string | number | undefined): string | undefined =>
  typeof value === "number" ? value.toString() : value?.trim() || undefined;

const toCoords = (
  lat: string | number | undefined,
  lng: string | number | undefined,
): CoordsTuple => [toCoord(lat), toCoord(lng)];

const hasCoords = ([lat, lng]: CoordsTuple) => Boolean(lat && lng);

/** Location keys are mutually exclusive – anything not passed is removed. */
const locationEntries = ({
  locationId,
  coords: [lat, lng] = [undefined, undefined],
}: { locationId?: string; coords?: CoordsTuple } = {}): QueryEntry[] => [
  ["locationId", locationId || undefined],
  ["lat", lat],
  ["lng", lng],
];

const pickFirst = (rules: readonly QueryRule[]): readonly QueryEntry[] =>
  rules.find(([when]) => when)?.[1] ?? [];

const applyEntries = (query: URLSearchParams, entries: readonly QueryEntry[]) =>
  entries.forEach(([key, value]) =>
    isEmpty(value) ? query.delete(key) : query.set(key, String(value)),
  );

const resolveHotelDates = (
  start: string,
  end: string,
): readonly [start: string, end: string] => {
  const hotelStart =
    start && dayjs(start).isValid() ? start : getStableTodayIso();
  const hotelEnd =
    end && dayjs(end).isValid() && dayjs(end).isAfter(dayjs(hotelStart), "day")
      ? end
      : dayjs(hotelStart).add(1, "day").format("YYYY-MM-DD");
  return [hotelStart, hotelEnd];
};

/*************************************
 * Per-service query builders
 *********************************** */

const SERVICE_QUERY_BUILDERS: Record<
  SearchServiceKey,
  (ctx: SubmitContext) => readonly QueryEntry[]
> = {
  hotels: ({ hotel, hotelCoords, hotelSearch }) => {
    const [start, end] = resolveHotelDates(hotel.start, hotel.end);
    return [
      ["adults", hotel.adults],
      ["children", hotel.children],
      ["rooms", hotel.rooms],
      ["start", start],
      ["end", end],
      ["type", hotel.hasCoordinates ? "near_me" : "popular"],
      ["search", hotelSearch],
      ...pickFirst([
        [Boolean(hotelSearch), locationEntries()],
        [hotel.hasCoordinates, locationEntries({ coords: hotelCoords })],
        [true, locationEntries({ locationId: hotel.locationId })],
      ]),
    ];
  },

  rentals: ({ shared, sharedCoords }) => [
    ["start", shared.date || shared.start],
    ["end", undefined],
    ["search", undefined],
    ["vehicleType", shared.vehicleType],
    ["pickupType", shared.pickupType],
    ...pickFirst([
      [hasCoords(sharedCoords), locationEntries({ coords: sharedCoords })],
      [true, locationEntries({ locationId: shared.locationId })],
    ]),
  ],

  tour_act: ({ shared, sharedCoords, sharedSearch }) => {
    const date = shared.date || shared.start;
    return [
      ["date", date],
      ["start", date],
      ["search", sharedSearch],
      ...pickFirst([
        [
          Boolean(shared.locationId),
          locationEntries({ locationId: shared.locationId }),
        ],
        [hasCoords(sharedCoords), locationEntries({ coords: sharedCoords })],
        [true, locationEntries()],
      ]),
    ];
  },

  spa_msg: ({ shared, sharedCoords }) => {
    const date = shared.date || shared.start;
    return [
      ["date", date],
      ["start", date],
      ...pickFirst([
        [hasCoords(sharedCoords), locationEntries({ coords: sharedCoords })],
        [true, locationEntries({ locationId: shared.locationId })],
      ]),
    ];
  },

  discover: () => [],
};

const withQuery = (path: string, query: string) =>
  query ? `${path}?${query}` : path;

/**
 * Hook to handle search submission across hotel, rental, spa_msg, and tour_act services.
 *
 * - `newTab`     → opens the results page in a new tab.
 * - `currentTab` → updates the URL in place; result hooks read the URL, so data
 *                  is refetched without leaving the page.
 */
export const useSearchSubmit = ({
  activeService,
  searchParams,
  hotel,
  shared,
  onSearchSuccess,
  targetPathOverride,
}: UseSearchSubmitParams) => {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const [isNavigating, setIsNavigating] = useState(false);

  // Reset navigating state when search params change
  useEffect(() => {
    setIsNavigating(false);
  }, [searchParams]);

  const submitSearch = useCallback(
    async (mode: SearchSubmitMode = "currentTab"): Promise<boolean> => {
      if (!activeService) return false;

      const serviceKey = toSearchServiceKey(activeService);
      const servicePath = getSearchPathByLabel(activeService);
      const basePath = targetPathOverride ?? servicePath;
      if (!basePath) return false;

      const query = new URLSearchParams(searchParams.toString());
      applyEntries(
        query,
        SERVICE_QUERY_BUILDERS[serviceKey]({
          hotel,
          shared,
          hotelCoords: toCoords(hotel.lat, hotel.lng),
          sharedCoords: toCoords(shared.lat, shared.lng),
          hotelSearch: hotel.search.trim(),
          sharedSearch: shared.search?.trim() ?? "",
        }),
      );
      const queryString = query.toString();

      const navigators: Record<SearchSubmitMode, () => void> = {
        newTab: () => {
          const localePrefix = locale && locale !== "en" ? `/${locale}` : "";
          const destination = `${localePrefix}${servicePath ?? basePath}`;
          openInNewTabWithHistory(withQuery(destination, queryString));
        },
        currentTab: () => {
          // Nothing changed → nothing to refetch, and searchParams won't change
          // to clear the loading state, so skip navigation entirely.
          const isSameUrl =
            basePath === pathname && queryString === searchParams.toString();
          if (isSameUrl) return;

          setIsNavigating(true);
          const method = targetPathOverride ? "replace" : "push";
          router[method](withQuery(basePath, queryString), { scroll: false });
        },
      };

      navigators[mode]();
      onSearchSuccess?.();
      return true;
    },
    [
      activeService,
      shared,
      searchParams,
      hotel,
      onSearchSuccess,
      targetPathOverride,
      router,
      pathname,
      locale,
    ],
  );

  return { submitSearch, isSubmitting: isNavigating };
};
